/**
 * SuiteTool — generic AI tool page powering 6 new endpoints.
 *   /youtube-suite/competitor-analysis
 *   /youtube-suite/thumbnail-brief
 *   /youtube-suite/video-repurpose
 *   /youtube-suite/upload-planner
 *   /content-suite/press-release
 *   /content-suite/case-study
 */
import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Users, Image as ImageIcon, Repeat, Upload, Newspaper, FileText,
  Sparkles, AlertCircle, Loader2, ArrowLeft, Copy, Check, Printer,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

const API = process.env.REACT_APP_BACKEND_URL || '';

const TOOL_CONFIG = {
  "competitor-analysis": {
    endpoint: "/api/suite/competitor-analysis",
    Icon: Users,
    eyebrow: "YouTube Suite",
    title: "Competitor Analysis",
    subtitle: "Compare your channel against up to 8 competitors. Spot content gaps, format wins and quick wins.",
    fields: [
      { name: "my_channel", label: "Your channel", type: "text", placeholder: "@yourchannel" },
      { name: "competitors", label: "Competitors (comma-separated)", type: "csv", placeholder: "@channel1, @channel2, @channel3" },
      { name: "niche", label: "Niche (optional)", type: "text", placeholder: "Personal finance for Indians" },
    ],
  },
  "thumbnail-brief": {
    endpoint: "/api/suite/thumbnail-brief",
    Icon: ImageIcon,
    eyebrow: "YouTube Suite",
    title: "Thumbnail Brief",
    subtitle: "Topic → a tight visual brief (concept, subject, colours, text overlay, A/B variant).",
    fields: [
      { name: "title", label: "Video title", type: "text", placeholder: "I tried 10 productivity apps so you don't have to" },
      { name: "niche", label: "Niche (optional)", type: "text", placeholder: "Productivity" },
      { name: "target_audience", label: "Target audience (optional)", type: "text", placeholder: "20-35 yo knowledge workers" },
      { name: "style", label: "Style", type: "select", options: ["bold", "minimal", "dramatic", "clean"], default: "bold" },
    ],
  },
  "video-repurpose": {
    endpoint: "/api/suite/video-repurpose",
    Icon: Repeat,
    eyebrow: "YouTube Suite",
    title: "Video Repurpose",
    subtitle: "Long video → Reels + Twitter thread + LinkedIn post + Blog outline. One input, four channels.",
    fields: [
      { name: "title", label: "Video title", type: "text", placeholder: "How I built a $1M SaaS in 18 months" },
      { name: "transcript_or_summary", label: "Transcript or summary", type: "textarea", placeholder: "Paste full transcript or a 100-word summary…", rows: 8 },
    ],
  },
  "upload-planner": {
    endpoint: "/api/suite/upload-planner",
    Icon: Upload,
    eyebrow: "YouTube Suite",
    title: "Upload Planner",
    subtitle: "Niche + cadence → 30-day publishing calendar with best days, times and weekly themes.",
    fields: [
      { name: "niche", label: "Niche", type: "text", placeholder: "Tech reviews · gadgets · phones" },
      { name: "audience", label: "Audience (optional)", type: "text", placeholder: "16-25 yo tech enthusiasts in India" },
      { name: "audience_timezone", label: "Timezone", type: "text", default: "Asia/Kolkata" },
      { name: "posts_per_week", label: "Videos / week", type: "number", default: 3, min: 1, max: 14 },
    ],
  },
  "press-release": {
    endpoint: "/api/suite/press-release",
    Icon: Newspaper,
    eyebrow: "Content Suite",
    title: "Press Release",
    subtitle: "AP-style press release with headline, dateline, lead, quotes, boilerplate and media contact.",
    fields: [
      { name: "headline_topic", label: "What's the news?", type: "text", placeholder: "Launching the AI Workspace for Teachers" },
      { name: "company", label: "Company", type: "text", placeholder: "MarkeeMix Labs" },
      { name: "spokesperson", label: "Spokesperson (optional)", type: "text", placeholder: "Kamlesh Singh, Founder" },
      { name: "key_points", label: "Key points (one per line)", type: "lines", placeholder: "First teacher-focused AI workspace\nBuilt for individual educators & institutes\nAvailable globally" },
      { name: "location", label: "Dateline location (optional)", type: "text", placeholder: "BENGALURU, INDIA" },
    ],
  },
  "case-study": {
    endpoint: "/api/suite/case-study",
    Icon: FileText,
    eyebrow: "Content Suite",
    title: "Case Study",
    subtitle: "Problem + solution + result inputs → polished case study with metrics, sections, client quote.",
    fields: [
      { name: "client", label: "Client", type: "text", placeholder: "Acme Corp" },
      { name: "industry", label: "Industry (optional)", type: "text", placeholder: "SaaS / D2C / Education" },
      { name: "problem", label: "The problem", type: "textarea", placeholder: "What pain point did the client face?", rows: 3 },
      { name: "solution", label: "The solution", type: "textarea", placeholder: "What did you/they do about it?", rows: 3 },
      { name: "results", label: "The results", type: "textarea", placeholder: "Quantitative + qualitative outcomes", rows: 3 },
    ],
  },
};

function FieldInput({ field, value, onChange }) {
  const base = "w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-[#A855F7] focus:ring-4 focus:ring-brand-100 outline-none text-sm font-sans bg-white";
  if (field.type === "select") {
    return (
      <select value={value ?? field.default ?? ""} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`field-${field.name}`}>
        {field.options.map((o) => <option key={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "number") {
    return <input type="number" min={field.min} max={field.max} value={value ?? field.default ?? ""} onChange={(e) => onChange(Number(e.target.value))} className={base} data-testid={`field-${field.name}`}/>;
  }
  if (field.type === "textarea") {
    return <textarea rows={field.rows || 5} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={base} data-testid={`field-${field.name}`}/>;
  }
  if (field.type === "lines") {
    return <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={base} data-testid={`field-${field.name}`}/>;
  }
  if (field.type === "csv") {
    return <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={base} data-testid={`field-${field.name}`}/>;
  }
  return <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={base} data-testid={`field-${field.name}`}/>;
}

function payloadFromForm(toolKey, form) {
  const out = { ...form };
  if (toolKey === "competitor-analysis") {
    out.competitors = (form.competitors || "").split(",").map((s) => s.trim()).filter(Boolean);
  }
  if (toolKey === "press-release") {
    out.key_points = (form.key_points || "").split("\n").map((s) => s.trim()).filter(Boolean);
  }
  return out;
}

// ─── Result renderers ────────────────────────────────────────────────
function CompetitorResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.my_channel} — Competitor Analysis</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">{data.niche}</Pill>
          <Pill variant="outline">{(data.competitors_analysed || []).length} competitors</Pill>
        </div>
        <p className="mt-4 text-sm text-slate-700"><b>Positioning gap:</b> {data.positioning_gap}</p>
      </PremiumCard>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Content gaps to fill</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">{(data.content_gaps || []).map((g, i) => <li key={i}>· {g}</li>)}</ul>
        </PremiumCard>
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Format recommendations</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">{(data.format_recommendations || []).map((g, i) => <li key={i}>· {g}</li>)}</ul>
        </PremiumCard>
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Thumbnail patterns to steal</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">{(data.thumbnail_patterns_to_steal || []).map((g, i) => <li key={i}>· {g}</li>)}</ul>
        </PremiumCard>
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Title patterns to steal</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">{(data.title_patterns_to_steal || []).map((g, i) => <li key={i}>· {g}</li>)}</ul>
        </PremiumCard>
      </div>
      <PremiumCard className="p-6 bg-brand-50/40" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-2">Upload cadence insight</h3>
        <p className="text-sm text-slate-700">{data.upload_cadence_insight}</p>
      </PremiumCard>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">30-day quick wins</h3>
        <div className="space-y-2">
          {(data["30_day_quick_wins"] || []).map((w, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
              <Pill variant={w.priority === "high" ? "brand" : "neutral"}>{w.priority}</Pill>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#111111]">{w.action}</p>
                <p className="text-xs text-slate-500 mt-1">{w.why}</p>
              </div>
            </div>
          ))}
        </div>
      </PremiumCard>
    </div>
  );
}

function ThumbnailBriefResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <Pill variant="brand" className="mt-3">{data.style}</Pill>
        <p className="mt-4 text-sm text-slate-700"><b>Concept:</b> {data.concept}</p>
      </PremiumCard>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PremiumCard className="p-5" hover={false}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Primary subject</p>
          <p className="mt-1 text-sm">{data.primary_subject}</p>
        </PremiumCard>
        <PremiumCard className="p-5" hover={false}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Background</p>
          <p className="mt-1 text-sm">{data.background}</p>
        </PremiumCard>
        <PremiumCard className="p-5" hover={false}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Facial expression</p>
          <p className="mt-1 text-sm">{data.facial_expression}</p>
        </PremiumCard>
        <PremiumCard className="p-5" hover={false}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Composition</p>
          <p className="mt-1 text-sm">{data.composition_layout}</p>
        </PremiumCard>
      </div>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Text overlay</h3>
        <div className="text-3xl font-heading font-bold text-[#A855F7]">{data.text_overlay?.main}</div>
        {data.text_overlay?.secondary && <div className="text-sm text-slate-500 mt-2">{data.text_overlay.secondary}</div>}
      </PremiumCard>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Colour palette</h3>
        <div className="flex flex-wrap gap-3">
          {(data.colour_palette || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-slate-200">
              <div className="w-8 h-8 rounded-md" style={{ backgroundColor: c }}/>
              <code className="text-xs">{c}</code>
            </div>
          ))}
        </div>
      </PremiumCard>
      <PremiumCard className="p-6 bg-brand-50/40" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-2">CTR psychology</h3>
        <p className="text-sm text-slate-700">{data.ctr_psychology}</p>
      </PremiumCard>
      {data.ab_variant && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-2">A/B variant to test</h3>
          <p className="text-sm text-slate-700">{data.ab_variant}</p>
        </PremiumCard>
      )}
    </div>
  );
}

function RepurposeResult({ data }) {
  const platforms = data.platforms || {};
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.source_title}</h2>
        <p className="mt-3 text-sm text-slate-700">{data.repurpose_tip}</p>
      </PremiumCard>
      {platforms.reels && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold mb-3">Reels / Shorts</h3>
          <div className="space-y-3">
            {platforms.reels.map((r, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200">
                <Pill variant="brand">Variant {i + 1}</Pill>
                <p className="mt-2 text-sm"><b>Hook:</b> {r.hook}</p>
                <p className="mt-2 text-sm whitespace-pre-line"><b>Script:</b> {r.script}</p>
                <p className="mt-2 text-xs text-slate-500"><b>Caption:</b> {r.caption}</p>
              </div>
            ))}
          </div>
        </PremiumCard>
      )}
      {platforms.twitter && platforms.twitter[0] && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold mb-3">X / Twitter thread</h3>
          <p className="text-sm font-semibold">{platforms.twitter[0].thread_intro}</p>
          <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-slate-700">
            {(platforms.twitter[0].tweets || []).map((t, i) => <li key={i}>{t}</li>)}
          </ol>
          <p className="mt-3 text-xs text-[#A855F7] font-semibold">CTA: {platforms.twitter[0].final_cta}</p>
        </PremiumCard>
      )}
      {platforms.linkedin && platforms.linkedin[0] && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold mb-3">LinkedIn post</h3>
          <p className="text-sm font-semibold">{platforms.linkedin[0].hook}</p>
          <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{platforms.linkedin[0].body}</p>
          <p className="mt-3 text-xs text-[#A855F7]"><b>Engagement Q:</b> {platforms.linkedin[0].cta}</p>
        </PremiumCard>
      )}
      {platforms.blog && platforms.blog[0] && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-lg font-semibold mb-3">Blog post outline</h3>
          <p className="text-sm font-semibold">{platforms.blog[0].title}</p>
          <p className="mt-1 text-xs text-slate-500">{platforms.blog[0].meta_description}</p>
          <ul className="mt-3 space-y-1 text-sm text-slate-700">
            {(platforms.blog[0].outline || []).map((s, i) => <li key={i}>· {s}</li>)}
          </ul>
          <p className="mt-3 text-sm text-slate-700 italic">{platforms.blog[0].opening_paragraph}</p>
        </PremiumCard>
      )}
      {(data.key_quotes || []).length > 0 && (
        <PremiumCard className="p-6 bg-brand-50/40" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Pull quotes</h3>
          {data.key_quotes.map((q, i) => (
            <p key={i} className="text-sm text-slate-700 italic">&ldquo;{q}&rdquo;</p>
          ))}
        </PremiumCard>
      )}
    </div>
  );
}

function UploadPlannerResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.niche} — Upload Plan</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">{data.posts_per_week} / week</Pill>
          <Pill variant="outline">{data.timezone}</Pill>
        </div>
        <p className="mt-4 text-sm text-slate-700"><b>Rationale:</b> {data.rationale}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Best days:</span>
          {(data.best_days || []).map((d) => <Pill key={d} variant="neutral">{d}</Pill>)}
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-3">Times:</span>
          {(data.best_publish_times || []).map((t) => <Pill key={t} variant="neutral">{t}</Pill>)}
        </div>
      </PremiumCard>
      <div className="space-y-3">
        {(data.weeks || []).map((w) => (
          <PremiumCard key={w.week} className="p-5" hover={false}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-heading text-base font-semibold">Week {w.week} — {w.theme}</h3>
              <Pill variant="brand">{(w.videos || []).length} videos</Pill>
            </div>
            <div className="space-y-2">
              {(w.videos || []).map((v, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
                  <Pill variant="neutral">{v.day} {v.time}</Pill>
                  <Pill variant="outline">{v.video_type}</Pill>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#111111]">{v.title}</p>
                    <p className="text-xs text-slate-500 mt-1">Hook: {v.hook}</p>
                  </div>
                </div>
              ))}
            </div>
          </PremiumCard>
        ))}
      </div>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Content pillars</h3>
        <div className="flex flex-wrap gap-2">{(data.content_pillars || []).map((p, i) => <Pill key={i} variant="brand">{p}</Pill>)}</div>
        <p className="mt-4 text-sm text-slate-500 italic">{data.consistency_tip}</p>
      </PremiumCard>
    </div>
  );
}

function PressReleaseResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-8" hover={false}>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">FOR IMMEDIATE RELEASE</p>
        <h2 className="mt-3 font-heading text-3xl font-bold text-[#111111] leading-tight">{data.headline}</h2>
        {data.subheadline && <p className="mt-2 text-lg text-slate-600">{data.subheadline}</p>}
        <p className="mt-6 text-sm text-slate-500"><b>{data.dateline}</b></p>
        <p className="mt-4 text-sm text-slate-800 leading-relaxed">{data.lead_paragraph}</p>
        <div className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed">
          {(data.body_paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
        </div>
        {(data.quotes || []).length > 0 && (
          <div className="mt-6 space-y-3">
            {data.quotes.map((q, i) => (
              <blockquote key={i} className="pl-4 border-l-2 border-[#A855F7]">
                <p className="text-sm italic text-slate-700">&ldquo;{q.quote}&rdquo;</p>
                <p className="mt-1 text-xs text-slate-500">— {q.speaker}, {q.role}</p>
              </blockquote>
            ))}
          </div>
        )}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">About</p>
          <p className="text-sm text-slate-700">{data.boilerplate}</p>
        </div>
        {data.media_contact && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Media contact</p>
            <p className="text-sm text-slate-700">{data.media_contact.name} · {data.media_contact.email} · {data.media_contact.phone}</p>
          </div>
        )}
      </PremiumCard>
    </div>
  );
}

function CaseStudyResult({ data }) {
  return (
    <div className="space-y-6">
      <PremiumCard className="p-6" hover={false}>
        <h2 className="font-heading text-2xl font-bold text-[#111111]">{data.title}</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          <Pill variant="brand">{data.client}</Pill>
          {data.industry && <Pill variant="outline">{data.industry}</Pill>}
        </div>
        <p className="mt-4 text-sm text-slate-700"><b>TL;DR:</b> {data.tldr}</p>
      </PremiumCard>
      {(data.metrics || []).length > 0 && (
        <PremiumCard className="p-6" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Key metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.metrics.map((m, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/30">
                <div className="text-xs uppercase tracking-wider text-slate-500">{m.label}</div>
                <div className="mt-2 font-heading text-2xl font-bold text-[#111111]">{m.value}</div>
                {m.delta && <div className="text-xs text-emerald-700 font-semibold mt-1">{m.delta}</div>}
              </div>
            ))}
          </div>
        </PremiumCard>
      )}
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Challenge</h3>
        <p className="text-sm text-slate-700 leading-relaxed">{data.sections?.challenge}</p>
      </PremiumCard>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Approach</h3>
        <p className="text-sm text-slate-700 leading-relaxed">{data.sections?.approach}</p>
      </PremiumCard>
      <PremiumCard className="p-6" hover={false}>
        <h3 className="font-heading text-base font-semibold mb-3">Results</h3>
        <p className="text-sm text-slate-700 leading-relaxed">{data.sections?.results}</p>
      </PremiumCard>
      {(data.sections?.lessons || []).length > 0 && (
        <PremiumCard className="p-6 bg-brand-50/40" hover={false}>
          <h3 className="font-heading text-base font-semibold mb-3">Lessons</h3>
          <ul className="space-y-1.5 text-sm text-slate-700">{data.sections.lessons.map((l, i) => <li key={i}>· {l}</li>)}</ul>
        </PremiumCard>
      )}
      {data.client_quote && (
        <PremiumCard className="p-6" hover={false}>
          <blockquote className="pl-4 border-l-2 border-[#A855F7]">
            <p className="text-base italic text-slate-700">&ldquo;{data.client_quote.quote}&rdquo;</p>
            <p className="mt-2 text-sm text-slate-500">— {data.client_quote.speaker}</p>
          </blockquote>
        </PremiumCard>
      )}
      {data.social_pull_quote && (
        <PremiumCard className="p-6 bg-[#111111] text-white" hover={false}>
          <p className="font-heading text-xl font-semibold">{data.social_pull_quote}</p>
        </PremiumCard>
      )}
    </div>
  );
}

const RENDERERS = {
  "competitor-analysis": CompetitorResult,
  "thumbnail-brief": ThumbnailBriefResult,
  "video-repurpose": RepurposeResult,
  "upload-planner": UploadPlannerResult,
  "press-release": PressReleaseResult,
  "case-study": CaseStudyResult,
};

export default function SuiteTool() {
  const { pathname } = useLocation();
  const { tool: paramTool } = useParams();
  const navigate = useNavigate();
  const tool = paramTool || pathname.split("/").filter(Boolean).pop();
  const cfg = TOOL_CONFIG[tool];
  const suitePath = pathname.startsWith("/content-suite") ? "/content-suite" : "/youtube-suite";
  const suiteLabel = suitePath === "/content-suite" ? "Content Suite" : "YouTube Suite";

  const [form, setForm] = useState(() =>
    cfg ? Object.fromEntries(cfg.fields.map((f) => [f.name, f.default ?? ""])) : {}
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <PremiumNav />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-[#111111]">Tool not found</h1>
          <PremiumButton className="mt-6" icon={ArrowLeft} onClick={() => navigate(suitePath)}>Back to {suiteLabel}</PremiumButton>
        </div>
        <PremiumFooter />
      </div>
    );
  }

  const run = async () => {
    const required = cfg.fields.find((f) => !f.optional && (form[f.name] === undefined || form[f.name] === "")) ;
    // Allow optional fields to be empty (we marked them in label)
    if (required && !["optional", "(optional)"].some((s) => required.label.toLowerCase().includes(s))) {
      // Mild guard — let backend validate the rest
    }
    setLoading(true); setError(""); setResult(null);
    try {
      const payload = payloadFromForm(tool, form);
      const r = await axios.post(`${API}${cfg.endpoint}`, payload, { withCredentials: true, timeout: 120000 });
      setResult(r.data?.result || r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally { setLoading(false); }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* ignore */ }
  };

  const Renderer = RENDERERS[tool];

  return (
    <div className="min-h-screen bg-white font-sans" data-testid={`suite-tool-${tool}`}>
      <PremiumNav />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate(suitePath)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="suite-back"
        >
          <ArrowLeft className="w-4 h-4"/> Back to {suiteLabel}
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <SectionHeading
            eyebrow={<><cfg.Icon className="w-3 h-3 inline -mt-0.5 mr-1"/> {cfg.eyebrow}</>}
            title={cfg.title}
            subtitle={cfg.subtitle}
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="mt-8">
          <PremiumCard className="p-6" hover={false}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cfg.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.type === "lines" || f.type === "csv" ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{f.label}</label>
                  <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })}/>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500">Powered by Claude Haiku 4.5 · ~5-30s</p>
              <PremiumButton onClick={run} loading={loading} icon={Sparkles} size="lg" data-testid="suite-run">
                {loading ? "Generating…" : `Generate ${cfg.title}`}
              </PremiumButton>
            </div>
            {error && (
              <div className="mt-4 text-sm text-red-600 flex items-center gap-2" data-testid="suite-error">
                <AlertCircle className="w-4 h-4"/> {error}
              </div>
            )}
          </PremiumCard>
        </motion.div>

        {loading && (
          <div className="mt-10 flex flex-col items-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]"/>
            <div className="mt-3 text-sm">Generating with Claude Haiku 4.5…</div>
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="suite-result">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <h2 className="font-heading text-xl font-semibold text-[#111111]">Result</h2>
              <div className="flex items-center gap-2">
                <PremiumButton variant="secondary" size="sm" icon={copied ? Check : Copy} onClick={copyJson}>{copied ? "Copied" : "Copy JSON"}</PremiumButton>
                <PremiumButton variant="secondary" size="sm" icon={Printer} onClick={() => window.print()}>Print / PDF</PremiumButton>
              </div>
            </div>
            <Renderer data={result} />
          </motion.div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}
